// ChatPage with integrated sidebar - demonstrates the sidebar architecture

import { useListChatProfilesQuery } from '@platform/api';
import { SidebarChat, useSidebarChat } from '@platform/chat';
import {
  Button,
  FormControl,
  Icon,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@shared/ui';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutShell from '../../layout/LayoutShell';

export default function ChatPageWithSidebar() {
  const navigate = useNavigate();
  const [selectedProfile, setSelectedProfile] = useState('');
  const [debugMode, setDebugMode] = useState(false);

  const { data: profilesData, isLoading: profilesLoading } = useListChatProfilesQuery();
  // Normalize profile options to slug strings regardless of backend shape
  const profileOptions = useMemo(() => {
    const raw = profilesData?.profiles ?? [];
    return raw
      .map((p: any) => {
        const slug = typeof p === 'string' ? p : p?.slug;
        return typeof slug === 'string' ? slug.trim() : '';
      })
      .filter(Boolean);
  }, [profilesData]);

  // Once profiles load, ensure selection is valid (pick default or first available).
  useEffect(() => {
    if (profilesLoading) return;
    if (profileOptions.length === 0) {
      setSelectedProfile('');
      return;
    }
    if (!selectedProfile || !profileOptions.includes(selectedProfile)) {
      const next = profileOptions.includes('default') ? 'default' : profileOptions[0];
      setSelectedProfile(next);
    }
  }, [profilesLoading, profileOptions, selectedProfile]);

  // Use the sidebar chat hook
  const chat = useSidebarChat({
    convId: `chat-sidebar-${Date.now()}`,
    profile: selectedProfile,
    autoConnect: !!selectedProfile,
    context: {
      pageType: 'general',
      pageName: 'Chat',
      timestamp: Date.now(),
    },
    onToolCall: (tool) => {
      console.log('Tool call:', tool);
    },
    onError: (error) => {
      console.error('Chat error:', error);
    },
  });

  // Render the sidebar - pass ALL entities, let SidebarChat handle filtering
  const rightSidebar = (
    <SidebarChat
      entities={chat.entities}
      convId={chat.convId}
      onSendMessage={chat.sendMessage}
      isLoading={chat.isStreaming}
      placeholder="Ask anything..."
      showHeader={false}
    />
  );
  return (
    <LayoutShell rightSidebar={rightSidebar} rightSidebarWidth={420}>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <Typography variant="h4">Chat with Sidebar</Typography>
          <div className="flex items-center gap-3">
            <Button
              size="small"
              variant="outlined"
              onClick={() => navigate('/chat')}
              startIcon={<Icon>view_stream</Icon>}
            >
              Inline Mode
            </Button>
            <Button
              size="small"
              variant={debugMode ? 'contained' : 'outlined'}
              onClick={() => setDebugMode(!debugMode)}
              startIcon={<Icon>{debugMode ? 'bug_report' : 'visibility_off'}</Icon>}
            >
              Debug {debugMode ? 'ON' : 'OFF'}
            </Button>
            {profilesLoading && <LinearProgress style={{ width: 100 }} />}
          </div>
        </div>

        {/* Profile selector */}
        <Paper className="p-4">
          <FormControl fullWidth>
            <InputLabel>Profile</InputLabel>
            <Select
              value={selectedProfile || ''}
              label="Profile"
              onChange={(e) => setSelectedProfile(e.target.value as string)}
              disabled={profilesLoading}
            >
              {!profilesLoading && profileOptions.length === 0 ? (
                <MenuItem value="">No profiles found</MenuItem>
              ) : null}
              {profileOptions.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>

        {/* About section */}
        <Paper className="p-4">
          <Typography variant="h6" className="mb-3">
            About Sidebar Mode
          </Typography>
          <Typography variant="body2" className="mb-2">
            The chat is now embedded in the right sidebar using the new architecture. This
            demonstrates how any page can integrate conversational AI while maintaining its own
            content and functionality.
          </Typography>

          <Typography variant="subtitle2" className="mt-4 mb-2">
            Key Features:
          </Typography>
          <ul className="list-disc ml-6 space-y-1 text-sm">
            <li>Chat persists as you interact with the page</li>
            <li>
              Pages can pass context to the chat via the{' '}
              <code className="bg-gray-100 px-1 rounded">context</code> prop
            </li>
            <li>Chat can trigger page actions via tool call handlers</li>
            <li>Conversation state managed centrally by Redux</li>
            <li>WebSocket streaming for real-time responses</li>
            <li>Responsive layout (sidebar hidden on mobile)</li>
          </ul>

          <Typography variant="subtitle2" className="mt-4 mb-2">
            Architecture:
          </Typography>
          <Typography variant="body2" className="mb-1">
            1. <strong>LayoutShell</strong> - Provides the container and layout
          </Typography>
          <Typography variant="body2" className="mb-1">
            2. <strong>useSidebarChat</strong> - Hook managing chat state and lifecycle
          </Typography>
          <Typography variant="body2" className="mb-1">
            3. <strong>SidebarChat</strong> - UI component rendering the chat interface
          </Typography>
          <Typography variant="body2">
            4. <strong>Redux timeline slice</strong> - Centralized conversation storage
          </Typography>
        </Paper>

        {/* Connection status */}
        {chat.error && (
          <Paper className="p-4 bg-red-50 border-l-4 border-red-500">
            <div className="flex items-start gap-2">
              <Icon sx={{ color: 'error.main' }}>error</Icon>
              <div>
                <Typography variant="subtitle2" color="error">
                  Connection Error
                </Typography>
                <Typography variant="body2" color="error">
                  {chat.error}
                </Typography>
              </div>
            </div>
          </Paper>
        )}

        {!chat.isConnected && !chat.error && (
          <Paper className="p-4 bg-yellow-50 border-l-4 border-yellow-500">
            <div className="flex items-center gap-2">
              <LinearProgress style={{ width: 40 }} />
              <Typography variant="body2">Connecting to chat server...</Typography>
            </div>
          </Paper>
        )}

        {chat.isConnected && (
          <Paper className="p-4 bg-green-50 border-l-4 border-green-500">
            <div className="flex items-center gap-2">
              <Icon sx={{ color: 'success.main' }}>check_circle</Icon>
              <Typography variant="body2">
                Connected to chat server - Conversation ID:{' '}
                <code className="bg-green-100 px-1 rounded text-xs">{chat.convId}</code>
              </Typography>
            </div>
          </Paper>
        )}

        {/* Demo content */}
        <Paper className="p-4">
          <Typography variant="h6" className="mb-3">
            Try It Out
          </Typography>
          <Typography variant="body2" className="mb-2">
            Use the chat sidebar on the right to:
          </Typography>
          <ul className="list-disc ml-6 space-y-1 text-sm">
            <li>Ask questions about your documents</li>
            <li>Request information or analysis</li>
            <li>Execute workflows and automations</li>
            <li>Get help with tasks</li>
          </ul>

          <Typography variant="body2" className="mt-4">
            The chat maintains context about this page and can interact with it through tool calls
            defined in the <code className="bg-gray-100 px-1 rounded">onToolCall</code> handler.
          </Typography>
        </Paper>

        {/* Entity count */}
        <Paper className="p-4">
          <Typography variant="subtitle2" className="mb-2">
            Conversation Stats
          </Typography>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Typography variant="body2" color="text.secondary">
                Total Entities
              </Typography>
              <Typography variant="h5">{chat.entities.length}</Typography>
            </div>
            <div>
              <Typography variant="body2" color="text.secondary">
                Streaming
              </Typography>
              <Typography variant="h5">{chat.isStreaming ? 'Yes' : 'No'}</Typography>
            </div>
          </div>

          {chat.entities.length > 0 && (
            <Button
              size="small"
              variant="outlined"
              onClick={chat.clearChat}
              startIcon={<Icon>delete</Icon>}
              className="mt-3"
            >
              Clear Conversation
            </Button>
          )}
        </Paper>
      </div>
    </LayoutShell>
  );
}
